import 'moment/locale/fr';

import {
  faBuilding,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';

import useResetTourMobileConnection from '../../api/super-admin/tours/useResetTourMobileConnection';
import useGetRequest from '../../api/useGetRequest';
import MainButton from '../../components/agv-button';
import Datatable from '../../components/agv-datatable';
import GenericModal from '../../components/agv-generic-modal';
import GenericPageContainer from '../../components/agv-generic-page-container';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import AgrivitechPagination from '../../components/agv-pagination';
import Status from '../../components/agv-status';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import { tour_status } from '../../enums/tour-status';

const PAGE_SIZE = 20;
const ALL_CLIENTS = '';

const TourReset = () => {
  const [filters, setFilters] = useState({
    limit: PAGE_SIZE,
    offset: 0,
    query: '',
    client_id: ALL_CLIENTS,
  });

  const [tourToReset, setTourToReset] = useState(null);
  const [moveToToday, setMoveToToday] = useState(true);
  const [resetChecklist, setResetChecklist] = useState(false);
  const [resetExecution, setResetExecution] = useState(false);

  // L'API impose le reset de checklist quand on purge l'exécution : sans ça la
  // tournée serait incohérente (étapes CREATED sous un statut STARTED).
  const checklistWillBeReset = resetChecklist || resetExecution;

  // Reflète la règle appliquée côté API : sans reset de checklist, une tournée
  // dont la checklist est faite repart en STARTED plutôt qu'en CREATED.
  const keepsStartedStatus =
    tourToReset?.is_checklist_done === 1 && !checklistWillBeReset;

  const url = useMemo(() => {
    const params = new URLSearchParams({
      limit: filters.limit,
      offset: filters.offset,
      ...(filters.query ? { query: filters.query } : {}),
      ...(filters.client_id ? { client_id: filters.client_id } : {}),
    });
    return `/super-admin/tours-for-reset?${params.toString()}`;
  }, [filters]);

  const { data, isLoading, isFirstLoad, mutate } = useGetRequest(url, 300);
  const { data: clientsData } = useGetRequest('/super-admin/clients', 0);
  const { isLoading: isResetting, resetTour } = useResetTourMobileConnection();

  const clientOptions = useMemo(
    () => [
      { value: ALL_CLIENTS, name: 'Tous les clients' },
      ...(clientsData?.clients || [])
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
        .map((client) => ({
          value: String(client.client_id),
          name: client.name,
        })),
    ],
    [clientsData]
  );

  const handleUpdateParam = useCallback((key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
      ...(key !== 'offset' && { offset: 0 }),
    }));
  }, []);

  const openResetModal = (tour) => {
    setTourToReset(tour);
    setMoveToToday(true);
    setResetChecklist(false);
    setResetExecution(false);
  };

  const handleConfirmReset = async () => {
    const result = await resetTour(
      tourToReset?.tour_id,
      moveToToday,
      resetChecklist,
      resetExecution
    );

    if (result) {
      setTourToReset(null);
      mutate();
    }
  };

  return (
    <GenericPageContainer
      title={'Reset de tournée'}
      subtitle={
        "Outil interne : rend une tournée à nouveau connectable depuis l'application mobile"
      }
      filters={
        <>
          <SearchFilter
            value={filters.query}
            setValue={(value) => handleUpdateParam('query', value)}
          />
          <SelectFilter
            value={filters.client_id}
            onChange={(value) => handleUpdateParam('client_id', value)}
            options={clientOptions}
            icon={faBuilding}
          />
        </>
      }
    >
      <div className="tw-mb-4 tw-flex tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-p-3">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="tw-mt-0.5 tw-size-4 tw-shrink-0 tw-text-amber-500"
        />
        <div className="tw-text-sm tw-text-amber-800">
          <p className="tw-m-0 tw-font-medium">
            Cet outil écrit directement en base, tous clients confondus.
          </p>
          <p className="tw-m-0 tw-mt-1">
            Le mobile ne peut se connecter qu&apos;à une tournée non finalisée
            et datée du jour. Le déblocage lève ces deux verrous et efface les
            traces de signature, sans toucher aux données d&apos;exécution. La
            remise à zéro complète des étapes est proposée séparément dans la
            fenêtre de confirmation.
          </p>
        </div>
      </div>

      <Datatable
        isLoading={isLoading}
        isFirstLoading={isFirstLoad && isLoading}
        header={[
          'Tournée',
          'Client',
          'Véhicule',
          'Date de tournée',
          'Étapes',
          'Statut',
          '',
        ]}
        data={data?.items || []}
        values={(tour) => [
          <span
            key={`${tour?.tour_id}_key`}
            className="tw-font-medium tw-text-slate-900"
          >
            {tour?.tour_key}
          </span>,
          <span key={`${tour?.tour_id}_client`} className="tw-text-slate-600">
            {tour?.client_name}
          </span>,
          <span key={`${tour?.tour_id}_vehicle`} className="tw-text-slate-600">
            {tour?.imat || tour?.vehicule_key}
          </span>,
          <span key={`${tour?.tour_id}_date`} className="tw-text-slate-600">
            {tour?.starting_date
              ? moment(tour.starting_date).format('DD MMM YYYY HH:mm')
              : '—'}
          </span>,
          <span key={`${tour?.tour_id}_steps`} className="tw-text-slate-600">
            {tour?.steps_count ?? 0}
          </span>,
          <div key={`${tour?.tour_id}_status`} className="tw-flex">
            <Status
              status={tour_status.properties[tour?.status]?.color}
              text={
                tour_status.properties[tour?.status]?.display_name ||
                tour?.status
              }
            />
          </div>,
          <div
            key={`${tour?.tour_id}_action`}
            className="tw-flex tw-justify-end"
          >
            <MainButton
              text={'Débloquer'}
              onClick={() => openResetModal(tour)}
            />
          </div>,
        ]}
      />

      {data?.items?.length > 0 && (
        <div className="tw-mt-4">
          <AgrivitechPagination
            data={data}
            setOffset={(value) => handleUpdateParam('offset', value)}
          />
        </div>
      )}

      <GenericModal
        isOpen={tourToReset != null}
        setIsOpen={() => setTourToReset(null)}
        title={`Débloquer la tournée ${tourToReset?.tour_key || ''}`}
        size={MODAL_SIZE_STYLE[MODAL_SIZE.XL]}
        mainAction={handleConfirmReset}
        mainActionText={
          resetExecution
            ? 'Confirmer la remise à zéro'
            : 'Confirmer le déblocage'
        }
        mainActionIsLoading={isResetting}
        secondaryAction={() => setTourToReset(null)}
        secondaryActionText={'Annuler'}
        secondaryActionIsDisabled={isResetting}
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-text-sm">
          <div className="tw-flex tw-flex-col tw-gap-1 tw-text-slate-600">
            <div>
              Client :{' '}
              <span className="tw-font-medium tw-text-slate-900">
                {tourToReset?.client_name}
              </span>
            </div>
            <div>
              Véhicule :{' '}
              <span className="tw-font-medium tw-text-slate-900">
                {tourToReset?.imat || tourToReset?.vehicule_key}
              </span>
            </div>
            <div>
              Statut actuel :{' '}
              <span className="tw-font-medium tw-text-slate-900">
                {tour_status.properties[tourToReset?.status]?.display_name ||
                  tourToReset?.status}
              </span>
            </div>
          </div>

          <div className="tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-bg-slate-50 tw-p-3">
            <p className="tw-m-0 tw-mb-2 tw-font-medium tw-text-slate-700">
              Ce qui va être modifié :
            </p>
            <ul className="tw-m-0 tw-list-disc tw-pl-5 tw-text-slate-600">
              <li>
                Statut repassé à{' '}
                <span className="tw-font-medium">
                  {keepsStartedStatus ? 'Démarrée' : 'Chargé'}
                </span>{' '}
                {keepsStartedStatus
                  ? '(le chauffeur reprend où il en était)'
                  : '(le flux de départ sera rejoué)'}
              </li>
              {moveToToday && (
                <li>Date de tournée replacée à aujourd&apos;hui</li>
              )}
              {checklistWillBeReset && <li>Checklist de départ à refaire</li>}
              <li>Signature effacée (lien, template et date de signature)</li>
              {resetExecution && (
                <>
                  <li>
                    Toutes les étapes remises à{' '}
                    <span className="tw-font-medium">Créée</span>, bons de
                    livraison à <span className="tw-font-medium">Planifié</span>
                  </li>
                  <li>
                    Photos, signatures, réserves, consignes, nettoyage,
                    carburant et kilométrages supprimés
                  </li>
                  <li>
                    Stock de palettes consignées recalculé (mouvements de la
                    tournée annulés)
                  </li>
                </>
              )}
            </ul>
          </div>

          <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-slate-700">
            <input
              type="checkbox"
              checked={moveToToday}
              onChange={(event) => setMoveToToday(event.target.checked)}
              className="tw-size-4 tw-cursor-pointer"
            />
            Replacer la tournée à la date du jour (heure de tournée conservée)
          </label>

          {/* Sans checklist faite, décocher n'aurait aucun effet : on masque.
              Purger l'exécution l'impose, la case devient alors non modifiable. */}
          {tourToReset?.is_checklist_done === 1 && (
            <label
              className={`tw-flex tw-items-center tw-gap-2 tw-text-slate-700 ${
                resetExecution ? 'tw-opacity-60' : 'tw-cursor-pointer'
              }`}
            >
              <input
                type="checkbox"
                checked={checklistWillBeReset}
                disabled={resetExecution}
                onChange={(event) => setResetChecklist(event.target.checked)}
                className="tw-size-4"
              />
              Faire refaire la checklist de départ au chauffeur
              {resetExecution && ' (imposé par la remise à zéro)'}
            </label>
          )}

          {resetChecklist && !resetExecution && (
            <div className="tw-flex tw-gap-3 tw-rounded tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-p-3">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="tw-mt-0.5 tw-size-4 tw-shrink-0 tw-text-amber-500"
              />
              <div className="tw-text-amber-800">
                Seul le flux de départ sera rejoué : les étapes déjà livrées
                resteront livrées. Pour refaire réellement la tournée depuis
                zéro, cochez la remise à zéro ci-dessous.
              </div>
            </div>
          )}

          <div className="tw-rounded tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-p-3">
            <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-font-medium tw-text-red-800">
              <input
                type="checkbox"
                checked={resetExecution}
                onChange={(event) => setResetExecution(event.target.checked)}
                className="tw-size-4 tw-cursor-pointer"
              />
              Remettre toutes les étapes à zéro (le chauffeur refait la tournée
              entière)
            </label>

            {resetExecution && (
              <div className="tw-mt-2 tw-text-red-800">
                <p className="tw-m-0 tw-font-medium">
                  Suppression définitive, sans retour arrière possible.
                </p>
                <p className="tw-m-0 tw-mt-1">
                  Le planning est conservé (étapes, bons de livraison, lignes de
                  commande), mais tout ce que le chauffeur a saisi est effacé.
                  Trois effets ne peuvent pas être annulés : les fichiers S3
                  (photos, signatures, PDF) restent stockés sans être
                  référencés, les statuts déjà transmis à Digitappro et AS400
                  seront <span className="tw-font-medium">renvoyés</span> quand
                  la tournée sera refaite, et les SMS et emails déjà envoyés
                  restent facturés au client.
                </p>
              </div>
            )}
          </div>

          {tourToReset?.concurrent_tours_today > 0 && moveToToday && (
            <div className="tw-flex tw-gap-3 tw-rounded tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-p-3">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="tw-mt-0.5 tw-size-4 tw-shrink-0 tw-text-red-500"
              />
              <div className="tw-text-red-800">
                Ce véhicule a déjà {tourToReset.concurrent_tours_today} autre
                {tourToReset.concurrent_tours_today > 1 ? 's' : ''} tournée
                {tourToReset.concurrent_tours_today > 1 ? 's' : ''} connectable
                {tourToReset.concurrent_tours_today > 1 ? 's' : ''}{' '}
                aujourd&apos;hui. Sans sélection de tournée côté mobile, le
                chauffeur sera connecté à celle dont le numéro est le plus petit
                — pas forcément celle-ci.
              </div>
            </div>
          )}
        </div>
      </GenericModal>
    </GenericPageContainer>
  );
};

export default TourReset;
